import React, { useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Icon,
  Stack,
  Chip,
  alpha,
  useTheme,
  styled,
  Skeleton,
} from "@mui/material";
import { SessionResponse } from "../../../types/pos.types";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { posService } from "../../../../../services/pos.service";

interface SessionSummaryProps {
  sessionData: SessionResponse;
  finalDenominations: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  };
  onClose: () => void;
}

const THEME_COLORS = {
  primary: "#1B7B6B",
  primaryLight: "#1E8078",
  primaryDark: "#166F68",
  success: "#059669",
  danger: "#B91C1C",
  background: "#FFFFFF",
  text: {
    primary: "#1A202C",
    secondary: "#4A5568",
    muted: "#718096",
  },
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  borderRadius: theme.shape.borderRadius,
  height: "100%",
}));

const DetailBox: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  return (
    <StyledPaper>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
        {value}
      </Typography>
    </StyledPaper>
  );
};

const LoadingSkeleton = () => (
  <Box>
    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box sx={{ flex: 1 }} key={i}>
          <StyledPaper>
            <Skeleton variant="text" width="40%" height={14} />
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          </StyledPaper>
        </Box>
      ))}
    </Stack>

    <Paper sx={{ p: 3, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
          mb: 2,
        }}
      >
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width="30%" height={32} />
      </Box>

      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="60%" height={24} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  </Box>
);

export const SessionSummary: React.FC<SessionSummaryProps> = ({
  sessionData,
  finalDenominations,
  onClose,
}) => {
  const [totalSalesLoading, setTotalSalesLoading] = React.useState(false);
  const [totalSales, setTotalSales] = React.useState(0);

  const calculateTotal = (items: Array<{ value: number; count: number }>) => {
    return items.reduce((sum, item) => sum + item.value * item.count, 0);
  };

  const finalTotal =
    calculateTotal(finalDenominations.bills) +
    calculateTotal(finalDenominations.coins);

  const getDuration = () => {
    const start = dayjs(sessionData.timestamps.start);
    const end = sessionData.timestamps.end
      ? dayjs(sessionData.timestamps.end)
      : dayjs();
    return `${end.diff(start, "hour")}h ${end.diff(start, "minute") % 60}m`;
  };

  const getTotalSales = () => {
    setTotalSalesLoading(true);
    posService
      .getSessionTotalSales({
        session_id: sessionData.id,
      })
      .then((res) => {
        const total = res.data;
        setTotalSales(total);
      })
      .catch(() => {
        toast.error(
          "Error happened while trying to fetch total sales of this session"
        );
      })
      .finally(() => {
        setTotalSalesLoading(false);
      });
  };

  useEffect(getTotalSales, [sessionData]);

  if (totalSalesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      {/* Session Details */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Cashier"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  person
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {sessionData.cashier.name}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Branch"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  store
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {sessionData.business?.branch_name || "-"}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Status"
            value={
              <Chip
                label="Completed"
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  textTransform: "capitalize",
                  fontWeight: 500,
                  color: THEME_COLORS.primary,
                  borderColor: alpha(THEME_COLORS.success, 0.3),
                  backgroundColor: alpha(THEME_COLORS.success, 0.05),
                }}
              />
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Duration"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  schedule
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {getDuration()}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: THEME_COLORS.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 1.5,
            borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
            mb: 2,
          }}
        >
          <Icon>summarize</Icon>
          Session Summary
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Start Time
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, mt: 0.5, color: THEME_COLORS.text.muted }}
            >
              {dayjs(sessionData.created_at).format("DD/MM/YYYY hh:mm A")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              End Time
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, mt: 0.5, color: THEME_COLORS.text.muted }}
            >
              {dayjs().format("DD/MM/YYYY hh:mm A")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Total Sales
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, mt: 0.5, color: THEME_COLORS.primary }}
            >
              BD {totalSales.toFixed(3)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Final Cash Count
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, mt: 0.5, color: THEME_COLORS.primary }}
            >
              BD {finalTotal.toFixed(3)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
